import { Controller } from "stimulus";
import debounce from "lodash/debounce";

export default class extends Controller {
  static targets = [
    "requiredTasks",
    "thresholdInput",
    "submitButton",
    "criteriaCheckbox",
    "taskCheckbox",
    "searchInput",
    "mandatoryTaskIdsContainer",
    "mandatoryTaskIdInput",
    "allTaskIdsContainer",
    "sessionCheckbox",
    "sessionSearchInput",
    "mandatorySessionIdsContainer",
    "mandatorySessionIdInput",
    "allSessionIdsContainer"
  ];
  static values = {
    taskCount: Number,
    completionPercentages: Object,
    completedTasksByLearner: Object,
    mandatoryTaskIds: Array,
    mandatorySessionIds: Array,
    attendedSessionsByLearner: Object,
    messageOne: String,
    messageOther: String
  };

  connect() {
    this.selectAllEnabled = null;
    this.changeListener = debounce(this._handleThresholdChange.bind(this), 300);
    this.boundHandleCheckboxChange = this._handleCheckboxChange.bind(this);
    this.boundHandleTasksLoaded = this._handleTasksLoaded.bind(this);
    this.boundHandleSessionCheckboxChange = this._handleSessionCheckboxChange.bind(this);
    this.boundHandleSessionsLoaded = this._handleSessionsLoaded.bind(this);

    this.thresholdInputTarget.addEventListener("change", this.changeListener);
    this.thresholdInputTarget.addEventListener("input", this.changeListener);

    // Listen for tasks/sessions loaded events to reattach listeners
    document.addEventListener('completion-criteria:tasks-loaded', this.boundHandleTasksLoaded);
    document.addEventListener('completion-criteria:sessions-loaded', this.boundHandleSessionsLoaded);

    this._attachCheckboxListeners();
    this._attachSessionCheckboxListeners();
    this.validateCriterias();
  }

  disconnect() {
    this.thresholdInputTarget.removeEventListener("change", this.changeListener);
    this.thresholdInputTarget.removeEventListener("input", this.changeListener);
    document.removeEventListener('completion-criteria:tasks-loaded', this.boundHandleTasksLoaded);
    document.removeEventListener('completion-criteria:sessions-loaded', this.boundHandleSessionsLoaded);

    this.taskCheckboxTargets.forEach(checkbox => {
      checkbox.removeEventListener("change", this.boundHandleCheckboxChange);
    });

    this.sessionCheckboxTargets.forEach(checkbox => {
      checkbox.removeEventListener("change", this.boundHandleSessionCheckboxChange);
    });
  }

  validateCriterias() {
    const isAnyChecked = this.criteriaCheckboxTargets.some(
      checkbox => checkbox.checked
    );

    this.criteriaCheckboxTargets.forEach(checkbox => {
      checkbox.required = !isAnyChecked;
    });

    // Only show modal if at least one criterion is selected
    if (isAnyChecked) {
      this._updateModalMessage();
    } else {
      // Clear modal message when no criteria selected
      this.submitButtonTarget.dataset.confirm = '';
    }
  }

  selectAll() {
    if (!this.hasAllTaskIdsContainerTarget) return;

    const contextTaskIds = JSON.parse(
      this.allTaskIdsContainerTarget.dataset.allTaskIds || '[]'
    );

    contextTaskIds.forEach(id => this._addMandatoryTaskId(id.toString()));

    this._syncCheckboxesWithHiddenFields();
    this._updateModalForMandatoryTasks();
  }

  deselectAll() {
    if (!this.hasAllTaskIdsContainerTarget) return;

    const contextTaskIds = JSON.parse(
      this.allTaskIdsContainerTarget.dataset.allTaskIds || '[]'
    );

    contextTaskIds.forEach(id => this._removeMandatoryTaskId(id.toString()));

    this._syncCheckboxesWithHiddenFields();
    this._updateModalForMandatoryTasks();
  }

  selectAllSessions() {
    if (!this.hasAllSessionIdsContainerTarget) return;

    const contextSessionIds = JSON.parse(
      this.allSessionIdsContainerTarget.dataset.allSessionIds || '[]'
    );

    contextSessionIds.forEach(id => this._addMandatorySessionId(id.toString()));

    this._syncSessionCheckboxesWithHiddenFields();
    this._updateModalMessage();
  }

  deselectAllSessions() {
    if (!this.hasAllSessionIdsContainerTarget) return;

    const contextSessionIds = JSON.parse(
      this.allSessionIdsContainerTarget.dataset.allSessionIds || '[]'
    );

    contextSessionIds.forEach(id => this._removeMandatorySessionId(id.toString()));

    this._syncSessionCheckboxesWithHiddenFields();
    this._updateModalMessage();
  }

  _attachCheckboxListeners() {
    this.taskCheckboxTargets.forEach(checkbox => {
      checkbox.removeEventListener("change", this.boundHandleCheckboxChange);
      checkbox.addEventListener("change", this.boundHandleCheckboxChange);
    });
  }

  _attachSessionCheckboxListeners() {
    this.sessionCheckboxTargets.forEach(checkbox => {
      checkbox.removeEventListener("change", this.boundHandleSessionCheckboxChange);
      checkbox.addEventListener("change", this.boundHandleSessionCheckboxChange);
    });
  }

  _handleTasksLoaded() {
    this._attachCheckboxListeners();
    this._syncCheckboxesWithHiddenFields();
  }

  _handleSessionsLoaded() {
    this._attachSessionCheckboxListeners();
    this._syncSessionCheckboxesWithHiddenFields();
  }

  _syncCheckboxesWithHiddenFields() {
    if (!this.hasMandatoryTaskIdsContainerTarget) return;

    // Get selected IDs from hidden fields
    const selectedIds = new Set(
      this.mandatoryTaskIdInputTargets.map(input => input.value)
    );

    this.taskCheckboxTargets.forEach(checkbox => {
      const taskId = checkbox.dataset.taskId;
      const shouldBeChecked = selectedIds.has(taskId);
      // Sync checkbox state with hidden fields
      if (checkbox.checked !== shouldBeChecked) {
        checkbox.checked = shouldBeChecked;

        // Update UI
        const optionalText = checkbox.closest('.task-checkbox-header')
          .querySelector('.task-optional-text');
        if (optionalText) {
          optionalText.classList.toggle('hidden', shouldBeChecked);
        }
      }
    });

    this._updateModalForMandatoryTasks();
  }

  _syncSessionCheckboxesWithHiddenFields() {
    if (!this.hasMandatorySessionIdsContainerTarget) return;

    const selectedIds = new Set(
      this.mandatorySessionIdInputTargets.map(input => input.value)
    );

    this.sessionCheckboxTargets.forEach(checkbox => {
      const sessionId = checkbox.dataset.sessionId;
      const shouldBeChecked = selectedIds.has(sessionId);
      if (checkbox.checked !== shouldBeChecked) {
        checkbox.checked = shouldBeChecked;

        const optionalText = checkbox.closest('.session-checkbox-header')
          .querySelector('.session-optional-text');
        if (optionalText) {
          optionalText.classList.toggle('hidden', shouldBeChecked);
        }
      }
    });

    this._updateModalMessage();
  }

  /**
   * Handles changes to the threshold input and updates the required tasks display.
   * - Validates the threshold input to ensure it is a number between 1 and 100.
   * - Updates the UI to display the required number of tasks (minimum 1).
   * - Updates modal message if needed.
  */
  _handleThresholdChange() {
    if (this.taskCountValue === 0) return;

    const threshold = parseInt(this.thresholdInputTarget.value, 10);

    if (isNaN(threshold) || threshold < 1 || threshold > 100) {
      this.submitButtonTarget.dataset.confirm = '';
      return;
    }

    const requiredTasks = Math.round((this.taskCountValue * threshold) / 100);
    this.requiredTasksTarget.innerText = requiredTasks > 0
      ? requiredTasks.toString()
      : "1";

    this._updateModalMessage();
  }

  _handleCheckboxChange(event) {
    const taskId = event.target.dataset.taskId;
    const isChecked = event.target.checked;

    // Update hidden fields
    if (isChecked) {
      this._addMandatoryTaskId(taskId);
    } else {
      this._removeMandatoryTaskId(taskId);
    }

    // Update UI
    event.target.closest('.task-checkbox-header')
      .querySelector('.task-optional-text')
      .classList.toggle('hidden', isChecked);

    this._updateModalForMandatoryTasks();
  }

  _handleSessionCheckboxChange(event) {
    const sessionId = event.target.dataset.sessionId;
    const isChecked = event.target.checked;

    if (isChecked) {
      this._addMandatorySessionId(sessionId);
    } else {
      this._removeMandatorySessionId(sessionId);
    }

    event.target.closest('.session-checkbox-header')
      .querySelector('.session-optional-text')
      .classList.toggle('hidden', isChecked);

    this._updateModalMessage();
  }

  _updateModalForMandatoryTasks() {
    this._updateModalMessage();
  }

  _updateModalMessage() {
    const thresholdActive = this._isThresholdActive();
    const mandatoryTasksActive = this._isMandatoryTasksActive();
    const mandatorySessionsActive = this._isMandatorySessionsActive();
    const threshold = thresholdActive ? parseInt(this.thresholdInputTarget.value, 10) : 0;
    const selectedTaskIds = this._getSelectedMandatoryTaskIds();
    const selectedSessionIds = this._getSelectedMandatorySessionIds();
    let criteriaMetCount = 0;

    // Evaluate every non-completed learner exactly once, keyed by membership
    // id. Each dataset is a {membershipId: value} map; a learner missing from
    // a map simply has no data for that criterion (0% / no tasks / no
    // attendance). The union of keys is the set of learners with any progress —
    // the only ones who can satisfy an active criterion.
    const membershipIds = new Set([
      ...Object.keys(this.completionPercentagesValue),
      ...Object.keys(this.completedTasksByLearnerValue),
      ...Object.keys(this.attendedSessionsByLearnerValue)
    ]);

    membershipIds.forEach(membershipId => {
      // checks threshold
      const percentage = this.completionPercentagesValue[membershipId] || 0;
      const thresholdMet = !thresholdActive || percentage >= threshold;
      // checks mandatory tasks
      const completedTaskIds = this.completedTasksByLearnerValue[membershipId] || [];
      const hasCompletedAllMandatoryTasks = selectedTaskIds.every(
        taskId => completedTaskIds.includes(taskId)
      );
      const mandatoryTasksMet = !mandatoryTasksActive || hasCompletedAllMandatoryTasks;
      // checks mandatory sessions
      const attendedSessionIds = this.attendedSessionsByLearnerValue[membershipId] || [];
      const hasAttendedAllMandatorySessions = selectedSessionIds.every(
        sessionId => attendedSessionIds.includes(sessionId)
      );
      const mandatorySessionsMet = !mandatorySessionsActive || hasAttendedAllMandatorySessions;

      if (thresholdMet && mandatoryTasksMet && mandatorySessionsMet) {
        criteriaMetCount++;
      }
    });

    this._setModalConfirmationMessage(criteriaMetCount);
  }

  _setModalConfirmationMessage(criteriaMetCount) {
    let message = '';

    if (criteriaMetCount === 1) {
      message = this.messageOneValue;
    } else if (criteriaMetCount > 1) {
      // Replace first number in message with actual count
      message = this.messageOtherValue.replace(/\d+/, criteriaMetCount);
    }
    this.submitButtonTarget.dataset.confirm = message;
  }

  _addMandatoryTaskId(taskId) {
    if (!this.hasMandatoryTaskIdsContainerTarget) return;

    // Check if already exists
    const existingInput = this.mandatoryTaskIdInputTargets.find(
      input => input.value === taskId
    );
    if (existingInput) return;

    // Create new hidden input
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'mandatory_task_ids[]';
    input.value = taskId;
    input.dataset.completionCriteriaTarget = 'mandatoryTaskIdInput';
    this.mandatoryTaskIdsContainerTarget.appendChild(input);
  }

  _removeMandatoryTaskId(taskId) {
    if (!this.hasMandatoryTaskIdsContainerTarget) return;

    const input = this.mandatoryTaskIdInputTargets.find(
      input => input.value === taskId
    );
    if (input) {
      input.remove();
    }
  }

  _addMandatorySessionId(sessionId) {
    if (!this.hasMandatorySessionIdsContainerTarget) return;

    const existingInput = this.mandatorySessionIdInputTargets.find(
      input => input.value === sessionId
    );
    if (existingInput) return;

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'mandatory_session_ids[]';
    input.value = sessionId;
    input.dataset.completionCriteriaTarget = 'mandatorySessionIdInput';
    this.mandatorySessionIdsContainerTarget.appendChild(input);
  }

  _removeMandatorySessionId(sessionId) {
    if (!this.hasMandatorySessionIdsContainerTarget) return;

    const input = this.mandatorySessionIdInputTargets.find(
      input => input.value === sessionId
    );
    if (input) {
      input.remove();
    }
  }

  _isThresholdActive() {
    return this.criteriaCheckboxTargets
      .find(cb => cb.id === 'threshold-criteria')?.checked || false;
  }

  _isMandatoryTasksActive() {
    return this.criteriaCheckboxTargets
      .find(cb => cb.id === 'mandatory-tasks-criteria')?.checked || false;
  }

  _isMandatorySessionsActive() {
    return this.criteriaCheckboxTargets
      .find(cb => cb.id === 'mandatory-sessions-criteria')?.checked || false;
  }

  _getSelectedMandatoryTaskIds() {
    if (!this._isMandatoryTasksActive()) return [];
    if (!this.hasMandatoryTaskIdsContainerTarget) return [];

    // Read directly from hidden fields
    return this.mandatoryTaskIdInputTargets
      .map(input => parseInt(input.value, 10))
      .filter(id => !isNaN(id));
  }

  _getSelectedMandatorySessionIds() {
    if (!this._isMandatorySessionsActive()) return [];
    if (!this.hasMandatorySessionIdsContainerTarget) return [];

    return this.mandatorySessionIdInputTargets
      .map(input => parseInt(input.value, 10))
      .filter(id => !isNaN(id));
  }
}
